import { AnalysisDimensionFilter, useAnalysis, useAnalysisConfigurationData } from "@models/analysis";
import { AnalysisSortOrder } from "@models/analysis";
import { DrillDownParams } from "@models/analysisVisualization";
import { DashboardDateRange } from "@models/dashboard";
import { FilterConfig } from "@models/pipelineNode";
import { ApexVisualization, PivotTableVisualization, TableVisualization, SingleValueVisualization } from "@pages/Analysis/AnalysisScreen.page";
import { CustomerAcquisitionVisualization } from "@pages/Analysis/premium/CustomerAcquisition";
import { LTVPivotTableVisualization } from "@pages/Analysis/premium/LTVPivotTable";
import { getErrorMessage } from "@services/errors.service";
import { useState, useCallback } from "react";
import { useImmer } from "use-immer";
import AnalysisSources from "@pages/Sources/AnalysisSources.component";

interface AnalysisVisualizationProps {
    analysisId: string;
    loadingDimensionFilters?: boolean;
    dimensionFilters?: AnalysisDimensionFilter[];
    staticFilters?: FilterConfig;
    dateRange?: DashboardDateRange;
    onEdit?: () => void;
    disableDrilldown?: boolean;
}

const AnalysisVisualization = (props: AnalysisVisualizationProps) => {
    const analysis = useAnalysis(props.analysisId);
    const [pageSize, setPageSize] = useState(50);
    const [page, setPage] = useState(1);
    const [sortOrder, setSortOrder] = useImmer<AnalysisSortOrder[]>([]);

    const toggleSort = useCallback((entityType: string, entityId: string) => {
        // It goes ASC -> DESC -> OFF
        setSortOrder((draft) => {
            const idx = draft.findIndex(s => s.entity_id == entityId && s.entity_type == entityType);
            if (idx >= 0) {
                if (!draft[idx].ascending) {
                    draft.splice(idx, 1);
                } else {
                    draft[idx].ascending = !draft[idx].ascending;
                }
            } else {
                draft.push({
                    entity_id: entityId,
                    entity_type: entityType,
                    ascending: true,
                });
            }
        });
    }, []);

    const vizConfig = useAnalysisConfigurationData({
        analysis: analysis.data,
        sortOrder: sortOrder,
        page: page,
        perPage: pageSize,
        filters: props.staticFilters,
        dateRange: props.dateRange,
        dimensionFilters: props.dimensionFilters,
        customParameters: analysis.data?.custom_parameters,
    }, {
        enabled: !!props.analysisId && !!analysis.data
    });
    
    const onDrilldown = useCallback((params: DrillDownParams) => {
        // Create a copy of the analysis 
    }, []);

    // If no analysisId is provided, show a message instead of trying to load data
    if (!props.analysisId) {
        return <div className="text-center" style={{ padding: 0, minHeight: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className="text-muted">No chart selected</div>
            <small className="text-muted mb-1">Please select a chart in the component settings</small>
            {props.onEdit && (
                <button 
                    className="btn btn-sm btn-outline-primary"
                    onClick={props.onEdit}
                    title="Edit Dashboard Component"
                    style={{ width: 'fit-content', flexShrink: 0 }}
                >
                    <i className="mdi mdi-pencil me-1"></i>
                    Edit Component
                </button>
            )}
        </div>;
    }

    const renderVisualization = () => {
        if (!analysis.data || !vizConfig.data) {
            return <></>;
        }

        

        const commonProps = {
            measureIds: analysis.data.measure_ids,
            dimensionIds: analysis.data.dimension_ids,
            sortOrder: sortOrder,
            onToggleSort: toggleSort,
            onDrillDown: onDrilldown,
        };

        switch (analysis.data.visualization_type) {
            case 'SINGLE_VALUE':
                return <SingleValueVisualization
                    {...commonProps}
                    params={vizConfig.data.record}
                />;
            case 'TABLE':
                console.log('disableDrilldown', props.disableDrilldown);
                return <div className="h-100">
                    <TableVisualization
                        {...commonProps}
                        onChangePage={(newPage: number, newPageSize: number) => {
                            setPage(newPage);
                            setPageSize(newPageSize);
                        }}
                        pageSize={pageSize}
                        currentPage={page}
                        params={vizConfig.data.record}
                        disableDrilldown={props.disableDrilldown}
                    />
                </div>;
            case 'PIVOT_TABLE':
                return <PivotTableVisualization 
                    {...commonProps} 
                    params={vizConfig.data.record}
                />;
            case 'PREMIUM_LTV_PIVOT_TABLE':
                return <LTVPivotTableVisualization 
                    analysis={analysis.data}
                    data={vizConfig.data.record}
                    onDrillDown={onDrilldown}
                />;
            case 'PREMIUM_CUSTOMER_ACQUISITION':
                // Check if this is a table format (TABLE_BY_CUSTOMER)
                if (vizConfig.data.record.render_type === 'table') {
                    return <div className="h-100">
                        <TableVisualization
                            {...commonProps}
                            onChangePage={(newPage: number, newPageSize: number) => {
                                setPage(newPage);
                                setPageSize(newPageSize);
                            }}
                            pageSize={pageSize}
                            currentPage={page}
                            params={vizConfig.data.record}
                        />
                    </div>;
                }
                return <CustomerAcquisitionVisualization
                    analysis={analysis.data}
                    data={vizConfig.data.record}
                    onDrillDown={onDrilldown}
                />;
            case 'BAR':
            case 'LINE':
                return <ApexVisualization 
                    {...commonProps} 
                    params={vizConfig.data.record}
                    analysis={analysis.data}
                />;
            default:
                return <div className="text-center p-3">
                    Unsupported visualization type: {analysis.data.visualization_type}
                </div>;
        }
    };

    if (!!vizConfig.error && !vizConfig.isLoading) {
        return <div className="text-center p-3">
            <i className="mdi mdi-alert-circle me-1"></i>
            {getErrorMessage(vizConfig.error)}
        </div>;
    }

    if (!analysis.data || !vizConfig.data || props.loadingDimensionFilters) {
        return <div className="text-center p-3">
            <i className="mdi mdi-loading mdi-spin me-1"></i>
            Loading...
        </div>;
    }

    return (
        <div className="h-100 w-100 d-flex flex-column" style={{overflow: 'hidden'}}>
            {analysis.data && (
                <div className="d-flex justify-content-end mb-1">
                    <AnalysisSources analysis={analysis.data} className="text-muted font-13" />
                </div>
            )}
            <div style={{flex: '1 1 auto', minHeight: 0}}>
                {renderVisualization()}
            </div>
        </div>
    );
};

export default AnalysisVisualization;
